<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { getCurrentServer } from '@/utils/globalData'
import china2 from '~/assets/china2.png'
import icon1688 from '~/assets/icon1688.png'
import icontb from '~/assets/icontb.png'
import imagePng from '~/assets/image.png'
import interIcon from '~/assets/interIcon.png'
import internetionalExpressLogo from '~/assets/internationalExpress.png'
import storage from '~/assets/storage.png'
import { useMainStore } from '~/composables/store'
import { getLocalizedStoreField } from '~/utils/i18nJson.js'

interface Props {
  modelValue?: boolean
  showDoNotRemind?: boolean
  shopLogoUrl?: string
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: false,
  showDoNotRemind: true,
  shopLogoUrl: '',
})

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  'confirm': []
  'detail': []
  'doNotRemind': [checked: boolean]
}>()

const store = useMainStore()
const { t, locale } = useI18n()
const storeInfo = computed(() => store.getStoreInfoDataMap(getCurrentServer()) || {})

const storeTitle = computed(() => getLocalizedStoreField(
  storeInfo.value,
  'title',
  locale.value,
  t('brand.storeFallbackName'),
))
const storeLogoUrl = computed(() => getLocalizedStoreField(
  storeInfo.value,
  'logoUrl',
  locale.value,
  props.shopLogoUrl,
))

const visible = ref(props.modelValue)
const doNotRemindChecked = ref(false)

watch(() => props.modelValue, (val) => {
  visible.value = val
})

watch(visible, (val) => {
  emit('update:modelValue', val)
})

function handleConfirm() {
  if (doNotRemindChecked.value) {
    emit('doNotRemind', true)
  }
  emit('confirm')
  visible.value = false
}

function handleDetail() {
  emit('detail')
}

function handleClose() {
  visible.value = false
}
</script>

<template>
  <el-dialog v-model="visible" :close-on-click-modal="false" :close-on-press-escape="false" :show-close="true"
    :align-center="true" width="95vw" class="poko-service-modal" @close="handleClose">
    <div class="modal-body">
      <!-- Header -->
      <div class="modal-header">
        <h2 class="modal-title">
          {{ $t('pokoServiceModal.title', { store: storeTitle }) }}
        </h2>
        <p class="modal-subtitle">
          {{ $t('pokoServiceModal.subtitle', { store: storeTitle }) }}
        </p>
      </div>

      <!-- Steps: 三列并排 -->
      <div class="modal-steps">
        <!-- Step 1 -->
        <div class="step-card step-card--left">
          <div class="step-card__badge ">
            <img v-if="china2" :src="china2" alt="" class="step-card__badge-img" />
            <span v-else class="step-card__badge-placeholder">{{ storeTitle }}</span>
          </div>
          <!-- Step 1 Illustration: 150x150，优先使用店铺 logoUrl -->
          <img
            v-if="storeLogoUrl"
            :src="storeLogoUrl"
            alt=""
            class="step-card__illustration"
          />
          <div class="step-card__inner">
            <h3 class="step-card__title">
              <span class="step-card__num step-card__num--orange">01 </span>
              <span>{{ $t('pokoServiceModal.step1Title', '支付商品费用') }}</span>
            </h3>
            <p class="step-card__desc">
              {{ $t('pokoServiceModal.step1Desc', { store: storeTitle })
              }}
            </p>
          </div>
        </div>

        <!-- Connector: 按 Figma 图层顺序堆叠（进度条 → 仓库icon → 快递icon → logo徽章） -->
        <div class="modal-steps__mid">
          <!-- 仓库图标: Figma 110x110 top:35px opacity:0.3 -->
          <!-- <img
            src="/figma-assets/poko-service-modal/warehouse-icon.png"
            alt=""
            class="modal-steps__warehouse-icon"
          /> -->
          <!-- 快递图标: Figma 64x64 top:100px -->
          <img :src="storage" alt="" class="modal-steps__express-icon" />
          <!-- 平台 logo badge: Figma top:149px 3 x 28x28 -->
          <div class="modal-steps__logo-badges">
            <img :src="icon1688" alt="" class="modal-steps__logo-badge" />
            <img :src="icontb" alt="" class="modal-steps__logo-badge" />
            <img :src="imagePng" alt="" class="modal-steps__logo-badge" />
          </div>
        </div>

        <!-- Connector row: absolutely positioned, bars extend to badges -->
        <div class="modal-steps__connector-row">
          <span class="modal-steps__bar modal-steps__bar--orange" />
          <p class="modal-steps__connector-text">
            {{ $t('pokoServiceModal.connector', { store: storeTitle }) }}
          </p>
          <span class="modal-steps__bar modal-steps__bar--green" />
        </div>

        <!-- Step 2 -->
        <div class="step-card step-card--right">
          <div class="step-card__badge ">

            <img v-if="interIcon" :src="interIcon" alt="" class="step-card__badge-img" />
          </div>
          <!-- Step 2 Illustration: 150x150 -->
          <img :src="internetionalExpressLogo" alt="" class="step-card__illustration" />

          <div class="step-card__inner">
            <h3 class="step-card__title">
              <span class="step-card__num step-card__num--green">02 </span>
              <span>{{ $t('pokoServiceModal.step2Title', '支付物流费用') }}</span>
            </h3>
            <p class="step-card__desc">
              {{ $t('pokoServiceModal.step2Desc', { store: storeTitle }) }}
            </p>
          </div>
        </div>
      </div>

      <!-- Note -->
      <div class="modal-note">
        <span class="modal-note__icon i-ep:warning-filled" />
        <span class="modal-note__text">
          {{ $t('pokoServiceModal.note', { store: storeTitle }) }}
        </span>
      </div>

      <!-- Actions -->
      <div class="modal-actions">
        <el-button type="primary" class="btn-confirm" @click="handleConfirm">
          {{ $t('pokoServiceModal.confirmBtn', '我已了解，继续购买') }}
        </el-button>
        <el-button class="btn-detail" @click="handleDetail">
          {{ $t('pokoServiceModal.detailBtn', '查看详细流程') }}
        </el-button>
        <label v-if="showDoNotRemind" class="modal-actions__checkbox">
          <input v-model="doNotRemindChecked" type="checkbox" />
          <span>{{ $t('pokoServiceModal.doNotRemind', '不再提示') }}</span>
        </label>
      </div>
    </div>
  </el-dialog>
</template>

<style scoped lang="less">
// Design token values from Figma
@color-brand-primary: #FF7228;
@color-brand-hover: #FE5911;
@color-brand-active: #EF3E07;
@color-text-primary: #1A1817;
@color-text-secondary: #645A56;
@color-text-tertiary: #908380;
@color-fill-surface: #FFFFFF;
@color-border-default: #D4CECD;
@color-green-600: #16A34A;
@color-bg-orange: rgba(255, 171, 113, 0.15);
@color-bg-green: rgba(134, 239, 173, 0.15);
@radius-card: 12px;
@radius-sm: 8px;
@space-stack-gap: 20px;
@space-item-gap: 12px;
@space-text-gap: 4px;
@font-title-m: 24px;
@font-title-sm: 20px;
@font-body-l: 16px;
@font-body-m: 14px;
@font-helper: 12px;

.poko-service-modal {
  :deep(.el-dialog) {
    max-width: 1400px;
    border-radius: @radius-card;
  }

  :deep(.el-dialog__header) {
    padding: 0;
    margin: 0;
    position: absolute;
    right: 20px;
    top: 20px;
    z-index: 1;
    border-bottom: none;
  }

  :deep(.el-dialog__headerbtn) {
    width: 24px;
    height: 24px;
    top: 0;
    right: 0;
  }

  :deep(.el-dialog__headerbtn .el-dialog__close) {
    color: @color-text-secondary;
    font-size: 18px;
  }

  :deep(.el-dialog__body) {
    padding: 0;
    overflow: hidden;
  }
}

.modal-body {
  padding: @space-stack-gap @space-stack-gap;
  display: flex;
  flex-direction: column;
  gap: @space-item-gap;
  min-width: 0;
}

// ===== Header =====
.modal-header {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: @space-text-gap * 2;
  padding: 0 168px; // match Figma: px-[168px]
}

.modal-title {
  margin: 0;
  font-size: @font-title-m;
  font-weight: 600;
  line-height: 32px;
  color: @color-text-primary;
  text-align: center;
}

.modal-subtitle {
  margin: 0;
  font-size: @font-body-l;
  font-weight: 400;
  line-height: 24px;
  color: @color-text-secondary;
  text-align: center;
}

// ===== Steps row =====
.modal-steps {
  display: flex;
  align-items: stretch;
  gap: 0;
  min-width: 0;
  position: relative;
  justify-content: center;
}

// Step card wrapper (left / right)
.step-card {
  position: relative;
  flex: 0 1 386px;
  min-width: 240px;
  max-width: 386px;
  border-radius: @radius-card;
  overflow: visible; // allow illustration to extend beyond card boundary (left: -17px)
  padding: 56px 20px 20px 131px; // text starts at left:131px to make room for illustration
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  word-break: break-word;

  // Use a pseudo-element for the background so overflow:visible doesn't leak content
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: @radius-card;
    z-index: 0;
  }

  &--left {
    &::before {
      background: @color-bg-orange;
    }
  }

  &--right {
    &::before {
      background: @color-bg-green;
    }
  }

  // Ensure text content sits above the background pseudo-element
  >* {
    position: relative;
    z-index: 1;
  }
}

// 150x150 illustration (Figma nodes 4444:27079, 4444:27097)
.step-card__illustration {
  position: absolute;
  left: 5px;
  top: 15px;
  width: 130px;
  height: 130px;
  pointer-events: none;
  object-fit: contain;
  z-index: 2; // above ::before background
}

.step-card__badge {
  position: absolute;
  top: 12px;
  left: 50%;
  transform: translateX(-50%);
  max-width: 160px;
  height: 32px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2px 8px;
  opacity: 0.85;
  z-index: 2; // above ::before background

  &--orange {
    background: linear-gradient(135deg, @color-brand-primary, @color-brand-hover);
  }

  &--green {
    background: linear-gradient(135deg, @color-green-600, #4ADE80);
  }
}

.step-card__badge-img {
  max-height: 28px;
  max-width: 100%;
  object-fit: contain;
}

.step-card__badge-placeholder {
  font-size: @font-body-m;
  font-weight: 700;
  color: #fff;
}

.step-card__inner {
  display: flex;
  flex-direction: column;
  gap: @space-text-gap;
}

.step-card__title {
  margin: 0;
  font-size: @font-title-sm;
  font-weight: 700;
  line-height: 28px;
}

.step-card__num {
  &--orange {
    color: @color-brand-hover;
  }

  &--green {
    color: @color-green-600;
  }
}

.step-card__desc {
  margin: 0;
  font-size: @font-body-m;
  font-weight: 400;
  line-height: 20px;
  color: @color-text-secondary;
}

.step-card__caption {
  margin: 0;
  font-size: @font-helper;
  line-height: 16px;
  color: @color-text-secondary;
  text-align: center;
  flex-shrink: 0;
}

// ===== Middle connector =====
.modal-steps__mid {
  flex: 0 1 auto;
  min-width: 100px;
  max-width: 272px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  padding: 45px 8px 0;
  gap: 8px;
  position: relative;
}

// Warehouse icon: 110x110 (node 4444:27100)
.modal-steps__warehouse-icon {
  width: 110px;
  height: 110px;
  opacity: 0.3;
  object-fit: contain;
  flex-shrink: 0;
  
}

// Express icon: 64x64 (node 4444:27113)
.modal-steps__express-icon {
  width: 64px;
  height: 64px;
  object-fit: contain;
  flex-shrink: 0;
}

// Logo badges: 3 x 28x28 (nodes 4444:27116/27117/27118)
.modal-steps__logo-badges {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
}

.modal-steps__logo-badge {
  width: 28px;
  height: 28px;
  border-radius: 6.222px;
  object-fit: contain;
}

.modal-steps__connector-text {
  margin: 0;
  font-size: @font-body-m;
  font-weight: 600;
  line-height: 22px;
  white-space: nowrap;
  flex-shrink: 0;
  padding: 0 12px;
  color: @color-brand-primary;
}

.modal-steps__progress {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  flex-shrink: 0;
}

.modal-steps__connector-row {
  position: absolute;
  left: 0;
  right: 0;
  top: 17px; // align with badge vertical position
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 0;
  padding: 0 280px; // leave room for step-card badges (approx half badge width + some margin)
  z-index: 0;
  pointer-events: none;
}

.modal-steps__bar {
  height: 4px;
  border-radius: @radius-sm;
  flex: 1 1 0;
  min-width: 16px;

  &--orange {
    background: #FFAB71;
  }

  &--green {
    background: #86EFAD;
  }
}

.modal-steps__dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #D4CECD;
  flex-shrink: 0;

  &--active {
    background: @color-brand-primary;
  }
}

// ===== Note =====
.modal-note {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: @space-text-gap;
}

.modal-note__icon {
  width: 16px;
  height: 16px;
  color: @color-text-tertiary;
  flex-shrink: 0;
}

.modal-note__text {
  font-size: @font-helper;
  font-weight: 400;
  line-height: 18px;
  color: @color-text-tertiary;
}

// ===== Actions =====
.modal-actions {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: @space-stack-gap;
  flex-wrap: wrap;
}

.btn-confirm,
.btn-detail {
  font-size: @font-helper;
  font-weight: 500;
  padding: 8px 12px;
  border-radius: 4px;
}

:deep(.btn-confirm) {
  background-color: @color-brand-primary;
  border-color: @color-brand-primary;
  color: #fff;

  &:hover,
  &:focus {
    background-color: @color-brand-hover;
    border-color: @color-brand-hover;
  }

  &:active {
    background-color: @color-brand-active;
    border-color: @color-brand-active;
  }
}

:deep(.btn-detail) {
  background: @color-fill-surface;
  border: 1px solid @color-border-default;
  color: @color-text-secondary;

  &:hover,
  &:focus {
    border-color: #B6ADAA;
    color: @color-text-primary;
  }
}

.modal-actions__checkbox {
  display: flex;
  align-items: center;
  gap: @space-text-gap;
  cursor: pointer;
  user-select: none;

  input[type="checkbox"] {
    width: 16px;
    height: 16px;
    border: 1px solid #D9D9D9;
    border-radius: 2px;
    background: #fff;
    accent-color: @color-brand-primary;
    cursor: pointer;
    margin: 0;
  }

  span {
    font-size: @font-helper;
    color: #999;
  }
}

// ===== Responsive =====
@media (max-width: 1160px) {
  .poko-service-modal {
    :deep(.el-dialog) {
      width: 95vw !important;
    }
  }

  .modal-header {
    padding: 0 20px;
  }

  .modal-steps {
    flex-direction: column;
    gap: @space-item-gap;
    min-height: auto;

    // connector row becomes static in column mode
    .modal-steps__connector-row {
      position: static;
      padding: 0;
      gap: 8px;
      pointer-events: auto;
    }

    .modal-steps__bar {
      flex: 1 1 0;
    }
  }

  .step-card {
    width: 100%;
    padding: 56px 20px 20px 131px;

    &--left {
      background: @color-bg-orange;

      &::before {
        background: @color-bg-orange;
      }
    }

    &--right {
      background: @color-bg-green;

      &::before {
        background: @color-bg-green;
      }
    }
  }

  .step-card__illustration {
    left: 8px;
    width: 100px;
    height: 100px;
    top: 56px;
  }

  .modal-steps__mid {
    padding: 8px 0;
    flex-direction: row;
    flex-wrap: wrap;
    gap: @space-item-gap;
    max-width: none;
  }

  .modal-steps__warehouse-icon {
    width: 64px;
    height: 64px;
  }

  .modal-steps__express-icon {
    width: 48px;
    height: 48px;
  }

  .modal-steps__logo-badge {
    width: 22px;
    height: 22px;
  }

  .modal-title {
    font-size: 20px;
    white-space: normal;
  }

  .modal-subtitle {
    font-size: @font-body-m;
    white-space: normal;
  }
}

@media (max-width: 768px) {
  .modal-body {
    padding: 16px 12px;
    gap: 10px;
  }

  .modal-header {
    padding: 0 8px;
  }

  .modal-title {
    font-size: 18px;
    line-height: 26px;
  }

  .modal-subtitle {
    font-size: 13px;
    line-height: 20px;
  }

  .step-card {
    padding: 12px;
    text-align: center;

    &__badge,
    .step-card__illustration {
      display: none;
    }
  }

  .step-card__title {
    font-size: @font-body-l;
  }

  .modal-steps__connector-row {
    flex-wrap: wrap;
    justify-content: center;
  }

  .modal-steps__connector-text {
    white-space: normal;
    text-align: center;
    font-size: @font-helper;
  }

  .modal-actions {
    flex-direction: column;
    gap: @space-item-gap;

    .el-button {
      width: 100%;
    }
  }

  .modal-note__text {
    white-space: normal;
    text-align: center;
  }
}
</style>
